import capitalize from 'lodash/capitalize';
import { ChangeEvent } from 'react';

import { PropertyTypeMarket } from 'root/widgets';

import { Listing, storage } from 'root/symbiosis-widgets';

import {
  BRAZE_NEW_HOME_ALERT_CARD_TARGET,
  BRAZE_NEW_HOME_ALERT_CARD_TYPE,
  BRAZE_STORAGE_KEY,
  BREAKOUT_PRICE_K,
  BREAKOUT_PRICE_M,
} from './constant';
import type { RawNewHomeAlertData } from './types';

const widgetName = 'NewHomeAlertCardGroup';

export const formatPrice = (price: number) => {
  if (price >= BREAKOUT_PRICE_M) {
    return `${Math.round((price * 10) / BREAKOUT_PRICE_M) / 10}M`;
  }

  if (price >= BREAKOUT_PRICE_K) {
    return `${Math.round((price * 10) / BREAKOUT_PRICE_K) / 10}K`;
  }

  return price;
};

export const getNewHomeAlertDataFromStorage = async (
  brazeAPIKey,
  dependencies,
): Promise<Array<RawNewHomeAlertData> | [] | undefined> => {
  const HOME_ALERT_LOCAL_STORAGE_KEY = [BRAZE_STORAGE_KEY, brazeAPIKey].join('.');
  const brazeData = storage.getItem(HOME_ALERT_LOCAL_STORAGE_KEY);
  const { searchService, logError } = dependencies;

  if (!brazeData) {
    return [];
  }

  let alerts;

  // extract data from localStorage and filter the newHomeAlert card type
  try {
    const parsedBrazeData = JSON.parse(brazeData)?.v || [];
    const newHomeAlertCards = parsedBrazeData.filter(
      (data: any) =>
        data?.e?.new_home_alert_card &&
        data?.e?.widgetType === BRAZE_NEW_HOME_ALERT_CARD_TYPE &&
        data?.e?.bu === BRAZE_NEW_HOME_ALERT_CARD_TARGET &&
        data?.e?.web === 'true',
    );

    if (newHomeAlertCards.length === 0) {
      return [];
    }

    alerts = JSON.parse(newHomeAlertCards.map((data: any) => data?.e?.new_home_alert_card)[0]);
    alerts.forEach((alert: any) => {
      alert.searchParams = {
        ...alert.searchParams,
        sort: 'date',
        order: 'desc',
        recommendationSource: 'new-home-alerts',
        market: PropertyTypeMarket.RESIDENTIAL,
      };
    });
  } catch (error) {
    logError(widgetName, getNewHomeAlertDataFromStorage.name, error);
    return [];
  }

  try {
    const options = {
      data: JSON.stringify({ searches: alerts }),
    };

    const searchUrlResult = await searchService.generateUrl(options);

    if (searchUrlResult) {
      alerts.forEach((alert: any, index: number) => {
        alert.redirectUrl = searchUrlResult.data[index];
      });
    }

    return alerts;
  } catch (error) {
    logError(widgetName, getNewHomeAlertDataFromStorage.name, error);
  }

  return [];
};

export const createCardData = (rawHomeAlertData, cardConfig, districtConfig, onCardClick) =>
  rawHomeAlertData?.map((alert: any, index: number) => {
    let pgIcon = 'pgicon-district';

    if (alert?.searchParams?.mrtCode) {
      pgIcon = 'pgicon-train';
    }

    if (alert?.searchParams?.propertyId) {
      pgIcon = 'pgicon-building';
    }

    let listingType = capitalize(alert?.searchParams?.listingType.toLowerCase());
    const minPrice = Number.isInteger(alert?.searchParams?.minPrice)
      ? formatPrice(alert?.searchParams?.minPrice)
      : null;
    const maxPrice = Number.isInteger(alert?.searchParams?.maxPrice)
      ? formatPrice(alert?.searchParams?.maxPrice)
      : null;
    const alertTitle = alert?.searchParams?.districtCode
      ? districtConfig[alert?.searchParams?.districtCode.toUpperCase()]
      : alert?.searchText.split(',').pop();
    const alertHeader = alert?.newListings === 1 ? `${cardConfig.title.single}` : `${cardConfig.title.plural}`;
    listingType = listingType.toLowerCase() === Listing.Type.Sale ? 'Buy' : 'Rent';
    const distance = alert?.searchParams?.distance
      ? `${cardConfig.distanceText}`.replace('{{distance}}', alert?.searchParams?.distance)
      : '';

    const details: any = [{ text: listingType }];

    if (distance) {
      details.push({ text: distance });
    }

    if (alert?.searchParams?.minBed || alert?.searchParams?.minBath) {
      details.push([
        ...(alert?.searchParams?.minBed
          ? [
              {
                text: alert?.searchParams?.minBed,
                icon: { name: 'pgicon-bedroom' },
              },
            ]
          : []),
        ...(alert?.searchParams?.minBath
          ? [
              {
                text: alert?.searchParams?.minBath,
                icon: { name: 'pgicon-bathroom' },
              },
            ]
          : []),
      ]);
    }

    if (minPrice && maxPrice) {
      const price =
        listingType.toLowerCase() === Listing.Type.Sale
          ? `${minPrice}-${maxPrice}`
          : `${minPrice}-${maxPrice}${cardConfig.monthlyText}`;
      details.push({ text: `${cardConfig.currency} ${price}` });
    }

    return {
      id: alert.alertId,
      title: `${alert.newListings} ${alertHeader}`,
      area: {
        icon: pgIcon,
        text: alertTitle,
      },
      details,
      link: {
        href: alert?.redirectUrl,
        rel: 'noreferrer noopener',
      },
      onClick: (e: ChangeEvent<HTMLInputElement>) => {
        onCardClick(e, alert, index);
      },
    };
  });

export const filterNewHomeAlertCards = (rawCards: Array<any> | []) =>
  rawCards
    .filter((val) => val?.extras?.new_home_alert_card)
    .map((card) => {
      card.url = `${window.location.protocol}://${window.location.host}`;
      return card;
    });
